import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Input } from "@/Input";
import { useDebounce } from "^/common/useDebounce";
import { twMerge } from "tailwind-merge";
import { SearchAreaType } from "^/customer/home/useStoreSearch";

interface Address 
  {
    address_name: string;
    b_code: string;
    h_code: string;
    main_address_no: string;
    mountain_yn: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_h_name: string;
    region_3depth_name: string;
    sub_address_no: string;
    x: string;
    y: string;
}


interface AddressSearchProps {
  onSelect: (address: SearchAreaType) => void;
  className?: string;
  defaultValue?: string;
}

const AddressSearch: React.FC<AddressSearchProps> = ({
  onSelect,
  className,
  defaultValue = "",
}) => {
  const [query, setQuery] = useState(defaultValue);
  const [results, setResults] = useState<Address[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const debouncedQuery = useDebounce(query, 300);

  // defaultValue가 변경될 때 query 업데이트
  useEffect(() => {
    if (defaultValue !== query) {
      setQuery(defaultValue);
    }
  }, [defaultValue]);

  const searchAddress = useCallback(async (searchQuery: string) => {
    if (searchQuery.trim() === "") {
      setResults([]);
      return;
    }

    try {
      const response = await axios.get(
        `https://dapi.kakao.com/v2/local/search/address.json`,
        {
          headers: {
            Authorization: `KakaoAK ${import.meta.env.VITE_KAKAO_KEY}`,
          },
          params: {
            query: searchQuery,
            analyze_type: "similar",
          },
        }
      );
      // '동' 단위로 필터링하고 중복 제거
      const filtered = response.data.documents.filter(
        (doc: Address) => doc.address_name
      );

      const uniqueDong = Array.from(
        new Set(filtered.map((doc: Address) => doc.address_name))
      )
        .map((addressName) => {
          return filtered.find(
            (doc: Address) => doc.address_name === addressName
          );
        })
        .filter((doc): doc is Address => doc !== undefined);
      setResults(uniqueDong);
    } catch (error) {
      console.error("Error searching address:", error);
      setResults([]);
    }
  }, []);

  useEffect(() => {
    searchAddress(debouncedQuery);
  }, [debouncedQuery, searchAddress]);

  const handleSelect = (address: SearchAreaType) => {
    setQuery(address.address_name);
    onSelect(address);

    setResults([]);
    setIsFocused(false);
  };

  return (
    <div className={twMerge("relative w-full", className)}>
      <Input
        value={query}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setQuery(e.target.value)
        }
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 150)}
        placeholder="시/군/구로 검색"
        className="w-full"
      />
      {isFocused && results.length > 0 && (
        <ul className="overflow-auto absolute z-50 mt-1 w-full max-h-60 bg-white rounded-md border border-gray-300 shadow-lg">
          {results.map((address, index) => (
            <li
              key={index}
              onMouseDown={() => handleSelect(address as unknown as SearchAreaType)}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100"
            >
              <span className="font-bold text-orange-500">
                {address.region_3depth_name}
              </span>
              <span className="ml-2 text-gray-700">{address.address_name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSearch;
